import { FormHelperText } from '@chakra-ui/react'
import * as React from 'react'

import { Text } from '~ui/components/typography/Text/Text'

import { Box, Flex } from '../../layout/Box'
import { FORM_LABEL_SPACE_LEFT } from './formAccessories.config'

type TProps = RequiredChildren &
  Omit<PropsOf<typeof Text>, 'as' | 'display'> & {
    leftIcon?: React.ReactElement
  }

/**
 * text message bellow the input,
 * useful for messages that should help the user in filling the form correctly
 * use inside the `InputControl` component to connect it with near inputs
 */
export const InputHelperMessage: React.FC<TProps> = ({
  children,
  fontSize = `s14`,
  contrast = `low`,
  fontStyle = `regularItalic`,
  padding = { left: FORM_LABEL_SPACE_LEFT, bottom: `none`, right: `none`, top: `extraSmall` },
  leftIcon,
  ...textPops
}) => {
  return (
    // margin 0 to override chakra default margin, that caused the message text to be cropped
    <FormHelperText margin={0}>
      <Flex padding={padding} gap={`small`} alignItems={`center`}>
        {leftIcon}
        <Text
          {...textPops}
          as={`span`}
          fontSize={fontSize}
          contrast={contrast}
          fontStyle={fontStyle}
          display={`inline-block`}
        >
          {children}
        </Text>
      </Flex>
    </FormHelperText>
  )
}

type TErrorMessageProps = OmitSafe<TProps, 'leftIcon'>

/**
 * text message bellow the input,
 * useful for results of form validation
 * use inside the `InputControl` component to connect it with near inputs
 */
export const InputErrorMessage: React.FC<TErrorMessageProps> = ({
  children,
  fontStyle = `boldSemi`,
  ...textPops
}) => {
  return (
    <InputHelperMessage {...textPops} fontStyle={fontStyle}>
      {children}
    </InputHelperMessage>
  )
}

type TValidationMessageProps = OmitSafe<TProps, 'leftIcon'> & {
  isValid: boolean
}

/**
 * text message bellow the input,
 * with the indicator of the state (based on the `isValid` prop value),
 * useful for results of form validation
 * use inside the `InputControl` component to connect it with near inputs
 */
export const InputValidationMessage: React.FC<TValidationMessageProps> = ({
  children,
  isValid,
  ...textPops
}) => {
  return (
    <InputHelperMessage
      {...textPops}
      leftIcon={
        <Box
          width={`8px`}
          height={`8px`}
          radius={`rounded`}
          display={`inline-block`}
          bgColor={isValid ? `specialColors.success` : `specialColors.disabled`}
        />
      }
    >
      {children}
    </InputHelperMessage>
  )
}
